import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, User, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const sid = () => "s_" + Math.random().toString(36).slice(2) + Date.now();

export default function ChatWidget() {
    const [open, setOpen] = useState(false);
    const [sessionId] = useState(sid);
    const [name, setName] = useState("");
    const [contact, setContact] = useState("");
    const [started, setStarted] = useState(false);
    const [msgs, setMsgs] = useState([{ role: "bot", text: "Hi there ✨ I'm Signa, your Signature Shine assistant. Pop your name & email/phone in and ask me anything!" }]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [offer, setOffer] = useState(false);
    const [handed, setHanded] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [msgs, open]);

    // Poll for agent (admin) replies every 5s when chat started
    useEffect(() => {
        if (!started) return;
        let lastAt = null;
        const tick = async () => {
            try {
                const r = await api.get(`/chat/poll/${sessionId}${lastAt ? `?since=${encodeURIComponent(lastAt)}` : ""}`);
                if (r.data?.length) {
                    setMsgs((m) => [...m, ...r.data.map((x) => ({ role: "agent", text: x.agent }))]);
                    lastAt = r.data[r.data.length - 1].at;
                }
            } catch {}
        };
        const t = setInterval(tick, 5000);
        return () => clearInterval(t);
    }, [started, sessionId]);

    const start = () => { if (name && contact) setStarted(true); };

    const send = async () => {
        if (!input.trim() || busy) return;
        const msg = input.trim(); setInput(""); setMsgs((m) => [...m, { role: "user", text: msg }]); setBusy(true);
        try {
            const r = await api.post("/chat/message", { session_id: sessionId, message: msg, visitor_name: name, visitor_contact: contact });
            if (r.data.in_handoff) {
                // Team has taken over — don't show empty AI bubble
                if (!msgs.find((m) => m.role === "system" && m.text.includes("team"))) {
                    setMsgs((m) => [...m, { role: "system", text: "You're now connected with our team. We'll reply here shortly." }]);
                }
            } else if (r.data.reply) {
                setMsgs((m) => [...m, { role: "bot", text: r.data.reply }]);
            }
            if (r.data.offer_handoff) setOffer(true);
        } catch { setMsgs((m) => [...m, { role: "bot", text: "Connection hiccup — please try again." }]); }
        finally { setBusy(false); }
    };

    const handoff = async () => {
        try {
            await api.post("/chat/handoff", { session_id: sessionId, visitor_name: name, visitor_contact: contact, note: msgs.slice(-1)[0]?.text || "" });
            setHanded(true);
            setMsgs((m) => [...m, { role: "bot", text: "Done — our team has your conversation and will be in touch shortly. 💛" }]);
        } catch {}
    };

    return (
        <>
            <button data-testid="chat-toggle" onClick={() => setOpen(!open)} className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gold text-navy shadow-[0_8px_24px_rgba(212,175,55,0.6)] flex items-center justify-center hover:scale-110 transition-transform">
                {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
            </button>
            {open && (
                <div data-testid="chat-panel" className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[600px] bg-white border border-gold/40 shadow-2xl flex flex-col font-outfit">
                    <div className="bg-navy text-cream px-5 py-4 flex items-center gap-3 border-b border-gold/40">
                        <div className="w-9 h-9 rounded-full bg-gold flex items-center justify-center"><Sparkles className="w-4 h-4 text-navy" /></div>
                        <div><div className="text-sm font-semibold">Signa</div><div className="text-[10px] uppercase tracking-[0.2em] text-gold">Signature Shine assistant</div></div>
                    </div>
                    <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-cream-light">
                        {msgs.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[80%] px-3 py-2 text-sm ${m.role === "user" ? "bg-navy text-cream" : m.role === "agent" ? "bg-gold/20 border border-gold text-navy" : m.role === "system" ? "bg-cream border border-gold/40 text-navy italic text-center w-full" : "bg-white border border-cream-border text-navy"}`}>
                                    {m.role === "agent" && <span className="text-[10px] uppercase tracking-wider text-gold-dark block mb-1">Team</span>}
                                    {m.text}
                                </div>
                            </div>
                        ))}
                        {busy && <div className="text-xs text-muted-foreground italic">Signa is typing…</div>}
                        {offer && !handed && (
                            <button data-testid="chat-handoff-btn" onClick={handoff} className="w-full mt-2 border border-navy text-navy py-2 text-xs uppercase tracking-wider hover:bg-navy hover:text-gold flex items-center justify-center gap-2"><User className="w-3 h-3" /> Talk to a human</button>
                        )}
                    </div>
                    {!started ? (
                        <div className="p-4 border-t border-cream-border space-y-2 bg-white">
                            <input data-testid="chat-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="w-full border border-cream-border px-3 py-2 text-sm focus:outline-none focus:border-gold" />
                            <input data-testid="chat-contact" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Email or phone" className="w-full border border-cream-border px-3 py-2 text-sm focus:outline-none focus:border-gold" />
                            <button data-testid="chat-start" onClick={start} disabled={!name || !contact} className="w-full bg-gold text-navy py-2 text-xs uppercase tracking-wider font-semibold disabled:opacity-40">Start chatting</button>
                        </div>
                    ) : (
                        <div className="p-3 border-t border-cream-border bg-white flex gap-2">
                            <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type your question…" className="flex-1 border border-cream-border px-3 py-2 text-sm focus:outline-none focus:border-gold" />
                            <button data-testid="chat-send" onClick={send} disabled={busy} className="bg-navy text-gold px-3 disabled:opacity-50"><Send className="w-4 h-4" /></button>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
